import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlineCompanyForm } from "@/components/forms/InlineCompanyForm";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import { CompanyForm } from "@/components/forms/CompanyForm";
import { PersonForm } from "@/components/forms/PersonForm";
import {
  ZeroProjectUpdate,
  ZeroProjectWithRelations,
} from "@/hooks/useProjects";
import {
  CompanyUpdate,
  useCompanies,
  useCompany,
  useUpdateCompany,
} from "@/hooks/useCompanies";
import {
  PersonUpdate,
  usePeople,
  usePerson,
  useUpdatePerson,
} from "@/hooks/usePeople";
import { useStudios } from "@/hooks/useStudios";
import { Database } from "@/integrations/neon/types";
import { DatePicker } from "@/components/ui/date-picker";
import { formatDateForInput } from "@/lib/date-utils";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import {
  getDraftFieldKeyDownHandlers,
  useDraftField,
} from "@/hooks/useDraftField";
import { Pencil } from "lucide-react";
import { CopyFromProjectSelector } from "@/components/project-detail/CopyFromProjectSelector";

type ProjectStatus = Database["public"]["Enums"]["project_status"];
type ProjectType = Database["public"]["Enums"]["project_type"];
type ProductionMode = Database["public"]["Enums"]["production_mode"];

const projectStatuses: { value: ProjectStatus; label: string }[] = [
  { value: "initial_contact", label: "Initial Contact" },
  { value: "offer", label: "Offer" },
  { value: "confirmed", label: "Confirmed" },
  { value: "rejected", label: "Rejected" },
];

const projectTypes: { value: ProjectType; label: string }[] = [
  { value: "live", label: "Live" },
  { value: "film", label: "Film" },
  { value: "gfx_tracking", label: "GFX & Tracking" },
  { value: "consultancy", label: "Consultancy" },
];

const productionModes: { value: ProductionMode; label: string }[] = [
  { value: "remote", label: "Remote Production" },
  { value: "on_site", label: "On Site Production" },
];

function formatZeroDateForInput(value: number | string | null | undefined) {
  if (typeof value === "number") return formatDateForInput(new Date(value));
  return formatDateForInput(value ?? undefined);
}

function arraysEqual<T>(a: T[], b: T[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function normalizeText(value: string, required = false) {
  const trimmed = value.trim();
  if (!trimmed && !required) return null;
  return trimmed;
}

interface DraftTextFieldProps {
  label: string;
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
}

function DraftTextField({
  label,
  value,
  onCommit,
  placeholder,
  required = false,
  maxLength,
  disabled,
}: DraftTextFieldProps) {
  const [error, setError] = useState<string | null>(null);
  const draft = useDraftField<string, string | null | undefined>({
    value,
    fromShared: (shared) => shared ?? "",
    toShared: (draftValue) => normalizeText(draftValue, required),
    onCommit: (nextValue) => {
      if (required && !nextValue) {
        setError(`${label} is required`);
        return;
      }
      if (maxLength && (nextValue?.length ?? 0) > maxLength) {
        setError(`${label} is too long`);
        return;
      }
      setError(null);
      if ((nextValue ?? null) !== (value ?? null)) {
        onCommit(nextValue ?? null);
      }
    },
  });

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">{label}</label>
      <Input
        placeholder={placeholder}
        value={draft.draft}
        onFocus={draft.start}
        onChange={(event) => draft.setDraft(event.target.value)}
        onBlur={() => void draft.commit()}
        onKeyDown={getDraftFieldKeyDownHandlers(draft)}
        disabled={disabled}
      />
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

interface DraftTextareaFieldProps {
  label: string;
  value: string | null | undefined;
  onCommit: (value: string | null) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
}

function DraftTextareaField({
  label,
  value,
  onCommit,
  placeholder,
  maxLength,
  disabled,
}: DraftTextareaFieldProps) {
  const [error, setError] = useState<string | null>(null);
  const draft = useDraftField<string, string | null | undefined>({
    value,
    fromShared: (shared) => shared ?? "",
    toShared: (draftValue) => normalizeText(draftValue),
    onCommit: (nextValue) => {
      if (maxLength && (nextValue?.length ?? 0) > maxLength) {
        setError(`${label} is too long`);
        return;
      }
      setError(null);
      if ((nextValue ?? null) !== (value ?? null)) {
        onCommit(nextValue ?? null);
      }
    },
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      void draft.commit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      draft.cancel();
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">{label}</label>
      <Textarea
        placeholder={placeholder}
        className="min-h-[100px]"
        value={draft.draft}
        onFocus={draft.start}
        onChange={(event) => draft.setDraft(event.target.value)}
        onBlur={() => void draft.commit()}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

interface DraftDateFieldProps {
  label: string;
  value: number | string | null | undefined;
  onCommit: (value: string | null) => void;
  disabled?: boolean;
}

function DraftDateField({
  label,
  value,
  onCommit,
  disabled,
}: DraftDateFieldProps) {
  const draft = useDraftField<string, string | null>({
    value: formatZeroDateForInput(value) || null,
    fromShared: (shared) => shared ?? "",
    toShared: (draftValue) => draftValue || null,
    onCommit: (nextValue) => {
      const currentValue = formatZeroDateForInput(value) || null;
      if ((nextValue ?? null) !== currentValue) {
        onCommit(nextValue ?? null);
      }
    },
  });

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">{label}</label>
      <DatePicker
        value={draft.draft}
        onFocus={draft.start}
        onChange={draft.setDraft}
        onBlur={() => void draft.commit()}
        onKeyDown={getDraftFieldKeyDownHandlers(draft)}
        disabled={disabled}
      />
    </div>
  );
}

interface FieldBlockProps {
  label: string;
  children: ReactNode;
}

function FieldBlock({ label, children }: FieldBlockProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">{label}</label>
      {children}
    </div>
  );
}

interface ProjectFormProps {
  project: ZeroProjectWithRelations;
  onSubmit: (data: ZeroProjectUpdate) => void;
  isLoading?: boolean;
  disabled?: boolean;
  disabledReason?: string | null;
}

export function ProjectForm({
  project,
  onSubmit,
  isLoading,
  disabled = false,
  disabledReason,
}: ProjectFormProps) {
  const { data: companies } = useCompanies();
  const { data: allPeople } = usePeople();
  const { data: studios } = useStudios();

  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);

  const updateCompany = useUpdateCompany();
  const updatePerson = useUpdatePerson();

  const employees =
    allPeople?.filter(
      (p) => p.person_type === "employee" || p.person_type === "freelancer",
    ) ?? [];
  const employeesOnly =
    allPeople?.filter((p) => p.person_type === "employee") ?? [];
  const contacts = allPeople?.filter((p) => p.person_type === "contact") ?? [];

  const customerOptions =
    companies
      ?.filter((c) => c.types?.includes("customer"))
      .map((c) => ({
        value: c.id,
        label: c.name,
      })) ?? [];

  const employeeOptions = employeesOnly.map((p) => ({
    value: p.id,
    label: `${p.first_name} ${p.last_name}`,
  }));

  const staffOptions = employees.map((p) => ({
    value: p.id,
    label: `${p.first_name} ${p.last_name}`,
    subtitle: p.person_type === "freelancer" ? "Freelancer" : undefined,
  }));
  const studioOptions =
    studios?.map((studio) => ({
      value: studio.id,
      label: studio.name,
      subtitle: studio.location || undefined,
    })) ?? [];

  const status = project.status ?? "initial_contact";
  const types = (project.types as ProjectType[] | null | undefined) ?? [];
  const customerId = project.customerID ?? null;
  const contactId = project.contactID ?? null;
  const isConfirmed = status === "confirmed";
  const isLive = types.includes("live");

  const { data: customerData } = useCompany(customerId ?? undefined);
  const { data: contactData } = usePerson(contactId ?? undefined);

  const filteredContacts = customerId
    ? contacts.filter((p) => p.company_id === customerId)
    : contacts;

  const contactOptions = filteredContacts.map((p) => ({
    value: p.id,
    label: `${p.first_name} ${p.last_name}`,
    subtitle: p.companies?.name,
  }));

  const updateProject = (updates: ZeroProjectUpdate) => {
    if (!disabled) {
      onSubmit(updates);
    }
  };

  const handleUpdateCustomer = async (data: CompanyUpdate) => {
    if (!customerId) return;
    await updateCompany.mutateAsync({ id: customerId, updates: data });
    setEditCustomerDialogOpen(false);
  };

  const handleUpdateContact = async (data: PersonUpdate) => {
    if (!contactId) return;
    await updatePerson.mutateAsync({ id: contactId, updates: data });
    setEditContactDialogOpen(false);
  };

  const handleCustomerChange = (nextCustomerId: string | null) => {
    const updates: ZeroProjectUpdate = {
      customerID: nextCustomerId,
    };

    if (
      contactId &&
      nextCustomerId &&
      !contacts.some(
        (contact) =>
          contact.id === contactId && contact.company_id === nextCustomerId,
      )
    ) {
      updates.contactID = null;
    }

    const selectedCompany = companies?.find((c) => c.id === nextCustomerId);
    if (
      selectedCompany?.language === "de" ||
      selectedCompany?.language === "en" ||
      selectedCompany?.language === "fr"
    ) {
      updates.language = selectedCompany.language;
    }

    updateProject(updates);
  };

  const handleStatusChange = (nextStatus: ProjectStatus) => {
    const updates: ZeroProjectUpdate = {
      status: nextStatus,
    };

    if (nextStatus !== "confirmed") {
      updates.projectManagerID = null;
      updates.technicalResponsibleID = null;
      updates.equipmentResponsibleID = null;
    }

    updateProject(updates);
  };

  const handleTypesChange = (type: ProjectType, checked: boolean) => {
    const nextTypes = checked
      ? [...types, type]
      : types.filter((currentType) => currentType !== type);
    if (arraysEqual(types, nextTypes)) return;

    updateProject({
      types: nextTypes,
      ...(nextTypes.includes("live")
        ? {}
        : {
            technicalResponsibleID: null,
            equipmentResponsibleID: null,
          }),
    });
  };

  return (
    <>
      <div className="space-y-6">
        {disabled && disabledReason && (
          <p className="text-sm text-muted-foreground">{disabledReason}</p>
        )}
        <fieldset disabled={disabled} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DraftTextField
              label="Number"
              value={project.number}
              onCommit={(number) => updateProject({ number })}
              placeholder="Unique identifier"
              maxLength={50}
              disabled={disabled}
            />

            <DraftTextField
              label="Project Name *"
              value={project.name}
              onCommit={(name) => {
                if (name) updateProject({ name });
              }}
              placeholder="Project name"
              required
              maxLength={255}
              disabled={disabled}
            />
          </div>

          <FieldBlock label="Previous Edition">
            <CopyFromProjectSelector
              value={project.previousEditionProjectID ?? null}
              onChange={(previousEditionProjectID) =>
                updateProject({ previousEditionProjectID })
              }
              currentProjectId={project.id}
              placeholder="Select previous edition"
              disabled={disabled}
              testId="previous-edition-project"
            />
          </FieldBlock>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldBlock label="Customer">
              <div className="flex gap-1 items-start">
                <EntityCombobox
                  options={customerOptions}
                  value={customerId}
                  onChange={handleCustomerChange}
                  placeholder="Select customer"
                  searchPlaceholder="Search customers..."
                  emptyMessage="No customers found."
                  createLabel="Create new customer"
                  disabled={disabled}
                  renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                    <InlineCompanyForm
                      onSuccess={onSuccess}
                      onCancel={onCancel}
                      initialName={initialName}
                      defaultTypes={["customer"]}
                    />
                  )}
                />
                {customerId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setEditCustomerDialogOpen(true)}
                    title="Edit customer"
                    disabled={disabled}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </FieldBlock>

            <FieldBlock label="Contact Person">
              <div className="flex gap-1 items-start">
                <EntityCombobox
                  options={contactOptions}
                  value={contactId}
                  onChange={(nextContactId) =>
                    updateProject({ contactID: nextContactId })
                  }
                  placeholder="Select contact"
                  searchPlaceholder="Search contacts..."
                  emptyMessage="No contacts found."
                  createLabel="Create new contact"
                  disabled={disabled}
                  renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                    <InlinePersonForm
                      onSuccess={onSuccess}
                      onCancel={onCancel}
                      initialName={initialName}
                      defaultType="contact"
                      showTypeSelector={false}
                      companyId={customerId}
                    />
                  )}
                />
                {contactId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setEditContactDialogOpen(true)}
                    title="Edit contact person"
                    disabled={disabled}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </FieldBlock>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DraftDateField
              label="Event Start Date"
              value={project.eventStartDate}
              onCommit={(eventStartDate) =>
                updateProject({
                  eventStartDate,
                  ...(project.eventEndDate
                    ? {}
                    : { eventEndDate: eventStartDate }),
                })
              }
              disabled={disabled}
            />

            <DraftDateField
              label="Event End Date"
              value={project.eventEndDate}
              onCommit={(eventEndDate) =>
                updateProject({
                  eventEndDate:
                    eventEndDate ||
                    formatZeroDateForInput(project.eventStartDate) ||
                    null,
                })
              }
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DraftDateField
              label="Planning Start Date"
              value={project.planningStartDate}
              onCommit={(planningStartDate) =>
                updateProject({
                  planningStartDate,
                  ...(project.planningEndDate
                    ? {}
                    : { planningEndDate: planningStartDate }),
                })
              }
              disabled={disabled}
            />

            <DraftDateField
              label="Planning End Date"
              value={project.planningEndDate}
              onCommit={(planningEndDate) =>
                updateProject({
                  planningEndDate:
                    planningEndDate ||
                    formatZeroDateForInput(project.planningStartDate) ||
                    null,
                })
              }
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldBlock label="Status">
              <Select
                value={status}
                onValueChange={(value) =>
                  handleStatusChange(value as ProjectStatus)
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {projectStatuses.map((projectStatus) => (
                    <SelectItem
                      key={projectStatus.value}
                      value={projectStatus.value}
                    >
                      {projectStatus.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldBlock>

            <FieldBlock label="Language">
              <Select
                value={project.language ?? "de"}
                onValueChange={(language) =>
                  updateProject({ language: language as "de" | "en" | "fr" })
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldBlock>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldBlock label="Production Mode">
              <Select
                value={project.productionMode ?? "__none__"}
                onValueChange={(value) =>
                  updateProject({
                    productionMode:
                      value === "__none__" ? null : (value as ProductionMode),
                  })
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select production mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not set</SelectItem>
                  {productionModes.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldBlock>

            <DraftTextField
              label="Production Location"
              value={project.productionLocation}
              onCommit={(productionLocation) =>
                updateProject({ productionLocation })
              }
              placeholder="e.g. Studio A, Zurich"
              maxLength={500}
              disabled={disabled}
            />
          </div>

          <FieldBlock label="Studio">
            <EntityCombobox
              options={studioOptions}
              value={project.studioID ?? null}
              onChange={(studioID) => updateProject({ studioID })}
              placeholder="Select studio"
              searchPlaceholder="Search studios..."
              emptyMessage="No studios found."
              disabled={disabled}
            />
          </FieldBlock>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldBlock label="Account Manager">
              <EntityCombobox
                options={employeeOptions}
                value={project.accountManagerID ?? null}
                onChange={(accountManagerID) =>
                  updateProject({ accountManagerID })
                }
                placeholder="Select account manager"
                searchPlaceholder="Search employees..."
                emptyMessage="No employees found."
                createLabel="Create new employee"
                disabled={disabled}
                renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                  <InlinePersonForm
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    initialName={initialName}
                    defaultType="employee"
                    showTypeSelector={false}
                  />
                )}
              />
            </FieldBlock>
          </div>

          <FieldBlock label="Project Types">
            <div className="flex flex-wrap gap-4">
              {projectTypes.map((type) => (
                <label
                  key={type.value}
                  className="flex items-center space-x-2 text-sm font-normal cursor-pointer"
                >
                  <Checkbox
                    checked={types.includes(type.value)}
                    onCheckedChange={(checked) =>
                      handleTypesChange(type.value, checked === true)
                    }
                    disabled={disabled}
                  />
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
          </FieldBlock>

          {isConfirmed && (
            <>
              <FieldBlock label="Project Manager">
                <EntityCombobox
                  options={staffOptions}
                  value={project.projectManagerID ?? null}
                  onChange={(projectManagerID) =>
                    updateProject({ projectManagerID })
                  }
                  placeholder="Select project manager"
                  searchPlaceholder="Search staff..."
                  emptyMessage="No staff found."
                  createLabel="Create new staff member"
                  disabled={disabled}
                  renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                    <InlinePersonForm
                      onSuccess={onSuccess}
                      onCancel={onCancel}
                      initialName={initialName}
                      defaultType="employee"
                    />
                  )}
                />
              </FieldBlock>

              {isLive && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldBlock label="Technical Responsible">
                    <EntityCombobox
                      options={employeeOptions}
                      value={project.technicalResponsibleID ?? null}
                      onChange={(technicalResponsibleID) =>
                        updateProject({ technicalResponsibleID })
                      }
                      placeholder="Select"
                      searchPlaceholder="Search employees..."
                      emptyMessage="No employees found."
                      createLabel="Create new employee"
                      disabled={disabled}
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlinePersonForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultType="employee"
                          showTypeSelector={false}
                        />
                      )}
                    />
                  </FieldBlock>

                  <FieldBlock label="Equipment Responsible">
                    <EntityCombobox
                      options={employeeOptions}
                      value={project.equipmentResponsibleID ?? null}
                      onChange={(equipmentResponsibleID) =>
                        updateProject({ equipmentResponsibleID })
                      }
                      placeholder="Select"
                      searchPlaceholder="Search employees..."
                      emptyMessage="No employees found."
                      createLabel="Create new employee"
                      disabled={disabled}
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlinePersonForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultType="employee"
                          showTypeSelector={false}
                        />
                      )}
                    />
                  </FieldBlock>
                </div>
              )}
            </>
          )}

          <DraftTextareaField
            label="Notes"
            value={project.notes}
            onCommit={(notes) => updateProject({ notes })}
            placeholder="Project notes..."
            maxLength={5000}
            disabled={disabled}
          />
        </fieldset>

        {isLoading && (
          <p className="border-t border-border pt-4 text-right text-sm text-muted-foreground">
            Saving...
          </p>
        )}
      </div>

      <Dialog
        open={editCustomerDialogOpen}
        onOpenChange={setEditCustomerDialogOpen}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {customerData && (
            <CompanyForm
              company={customerData}
              onSubmit={handleUpdateCustomer}
              onCancel={() => setEditCustomerDialogOpen(false)}
              isLoading={updateCompany.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editContactDialogOpen}
        onOpenChange={setEditContactDialogOpen}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact Person</DialogTitle>
          </DialogHeader>
          {contactData && (
            <PersonForm
              person={contactData}
              onSubmit={handleUpdateContact}
              onCancel={() => setEditContactDialogOpen(false)}
              isLoading={updatePerson.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
